import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "axios";
import { ButtonSpinnerSlot } from "../ui/ButtonSpinner";

const UploadPDF = ({ courseId, onUploadSuccess, open, onClose }) => {
  const [name, setName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [errors, setErrors] = useState({ name: false, file: false });

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  // ✅ Allowed file types
  const allowedTypes = [
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
  ];

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file && !allowedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, file: true }));
      alert("Only PDF and Image files (PNG, JPG, JPEG, WEBP) are allowed.");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setErrors((prev) => ({ ...prev, file: false }));
  };

  // Handle name input
  const handleNameChange = (event) => {
    setName(event.target.value);
    setErrors((prev) => ({ ...prev, name: false }));
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!name.trim() || !selectedFile) {
      setErrors({
        name: !name.trim(),
        file: !selectedFile,
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("file", selectedFile);
    formData.append("courseId", courseId);
    formData.append("source", "Instructor");

    try {
      setUploading(true);
      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await axios.post(
        apiUrl + `/api/uploadPdf/${courseId}`, // You may rename backend endpoint to /uploadFile later
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "*/*",
            Authorization: getBasicAuthHeader(),
          },
        }
      );

      const fileUrl = response.data.url;
      alert("File uploaded successfully!");
      setUploadedUrl(fileUrl);

      if (onUploadSuccess) {
        onUploadSuccess(fileUrl);
      }

      setName("");
      setSelectedFile(null);
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload the file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            fontSize: "1.5rem",
          }}
        >
          Manage Course Files
        </DialogTitle>

        <DialogContent>
          <Card
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            elevation={4}
            sx={{
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center",
              backgroundColor: "#f9f9f9",
            }}
          >
            <CardContent>
              {/* Name Input */}
              <TextField
                fullWidth
                label="File Name"
                variant="outlined"
                value={name}
                onChange={handleNameChange}
                error={errors.name}
                helperText={errors.name ? "Name is required" : ""}
                sx={{ marginBottom: "16px" }}
              />

              {/* File Upload Input */}
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={handleFileChange}
                style={{
                  display: "block",
                  margin: "0 auto 8px",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  width: "80%",
                }}
              />

              {errors.file && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ marginBottom: "8px" }}
                >
                  Valid file (PDF or Image) is required
                </Typography>
              )}

              {/* Upload Button */}
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading || !name.trim() || !selectedFile}
                sx={{
                  width: "80%",
                  padding: "10px",
                  margin: "8px 0",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {uploading ? (
                  <ButtonSpinnerSlot size={24} />
                ) : (
                  "Upload File"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Uploaded File Info */}
          {uploadedUrl && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{
                marginTop: "16px",
                textAlign: "center",
                backgroundColor: "#e8f5e9",
                padding: "10px",
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "#2e7d32", fontWeight: "bold" }}
              >
                Uploaded File:{" "}
                <a
                  href={uploadedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {uploadedUrl}
                </a>
              </Typography>
            </motion.div>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadPDF;
